import { yup } from "dyl-components";
import applyCurrencySchema from "../products/applyCurrencySchema";

export const addPlanRequirements = (schema) => schema.test("at_least_one_plan_selected", "There should be at least one plan selected", (cart) => {
    return cart.filter(item => item.is_plan).length
}).test("should_have_tcr_products", "TCR products should be selected for texting", (cart) => (
    cart.findIndex(item => item.plan_features?.includes("text")) === -1 ||
    (cart.find(item => item.dyl_product_name === "tcr" && !item.removed) && cart.find(item => item.dyl_product_name?.includes("campaign_volume") && !item.removed))
));

export const cartSchema = {
    cart: yup
        .array()
        .min(1, "There should be at least one item selected")
        .test("at_least_one_item_selected", "There should be at least one item selected", (cart, context) => {
            const { pricing_schedule } = context.parent;
            if (!pricing_schedule) {
                return true;
            }
            return cart.filter(item => {
                return !item.pricing_schedule || item?.schedules?.includes(pricing_schedule);
            }).length
        }),
}

export const formatOrder = (items) => {
    return (items || [])
        .filter((item) => !Boolean(item.removed))
        .map((item) => {
            return {
                addons: item.addons?.map((addon_id) => addon_id) || [],
                product: {
                    cart_item_id: item.cart_item_id,
                    price_schedule: item.pricing_schedule,
                    product_id: item.id,
                    product_variation_id: item.variation_id,
                    quantity: Number(item.quantity),
                    discount: item.discount || 0,
                    life_time_discount: !item.life_time_discount ? undefined : item.life_time_discount,
                    discount_cycle: !item.life_time_discount ? item.discount_cycle || 1 : undefined,
                    ...(item?.dyl_product_name?.includes("_balance") && item.amount ? { amount: item.amount } : {}),
                },
            };
        });
};

export const formatCartItems = ({ cart, ...data }) => {
    const { pricing_schedule: master_filter } = data;
    return {
        ...data,
        items: (cart || []).filter(item => {
            return !item.pricing_schedule || !master_filter || item?.schedules?.includes(master_filter);
        }).map(
            ({
                id: product_id,
                pricing_schedule,
                variation_id: product_variation_id,
                quantity,
                schedules,
                amount,
                ...item
            }) => ({
                ...item,
                product: {
                    ...(amount ? { amount: Number(amount) } : {}),
                    product_id,
                    price_schedule: pricing_schedule ? master_filter || pricing_schedule : undefined,
                    product_variation_id,
                    quantity,
                    discount: item.discount || 0,
                    life_time_discount: item.life_time_discount || false,
                    discount_cycle: item.discount_cycle || 1,
                },
            })
        ),
    };
};

export const getDefaultCartItems = (items, account_term) => {
    return (items || []).map((item) => ({
        id: item.product_id,
        pricing_schedule: item.price_data?.model?.includes("recurring") || item.pricing_schedule ? account_term || item.pricing_schedule : null,
        variation_id: item.product_variation_id,
        quantity: item.quantity,
        addons: item.addon?.filter((addon) => !addon.unlinked)?.map((addon) => addon.id),
        cart_item_id: item.cart_item_id,
        removed: item.product_status === "inactive" ||
            item.product_status === "discontinued",
        discount: item.product_discount || 0,
        max_discount: item.max_discount || item.product_discount || 0,
        model: item.price_data?.model,
        shippable: item.shippable,
        schedules: item.price_data?.model?.includes("recurring") ? Object.keys(item.price_data?.price || {}).filter(frequency => Boolean(item.price_data?.price[frequency]?.active)) : [],
        allowed_products: item.plan_product_ids || [],
        is_plan: item.is_plan || item.product_type === "plan",
        is_reactivation: item.dyl_product_name === "account_reactivation",
        plan_features: item.plan_features || item.plan_feature_names || [],
        required_product_id: item.required_product_id || null,
        dyl_product_name: item.dyl_product_name || item.product_name,
        ...(item.dyl_product_name?.includes("_balance") ? { amount: item.amount || item.item_price } : {}),
        discount_cycle: item.discount_cycle || 1,
        life_time_discount: item.life_time_discount || false,
    }));
};

export const cartItemSchema = {
    removed: yup.boolean().nullable(true),
    quantity: yup
        .lazy(value => value !== 0 && !Boolean(value) ? yup.string().when("removed", {
            is: false,
            then: schema => schema.required("Invalid quantity"),
            otherwise: schema => schema.nullable(true)
        }) : yup.number().positive("Invalid quantity")
        .integer("Invalid quantity")
        .typeError("Invalid quantity")),
    pricing_schedule: yup.string().when("model", {
        is: value => value?.includes("recurring"),
        then: schema => schema.required("This field is reqired"),
        otherwise: schema => schema.nullable(true)
    }),
    amount: yup.mixed().when("dyl_product_name", {
        is: value => value === "account_balance" || value === "sms_balance",
        then: () => applyCurrencySchema(yup.number()).required("This field is required"),
        otherwise: schema => schema.nullable(true)
    }),
    discount: yup.number()
        .typeError("Please enter a valid rate")
        .min(0, "Please enter a valid rate")
        .max(100, "Please enter a valid rate")
        .test("maxDigitsAfterDecimal", "Please enter a valid rate", (number) =>
            /^\d+(\.\d{1,2})?$/.test(number)
        )
        .test({
            name: "should_not_be_more_than_max",
            test: function(value, context) {
                if (context.parent.removed) {
                    return true;
                }
                const max_discount = context.parent.max_discount;
                return value <= max_discount || this.createError({
                    message: `Exceeds max`,
                    path: this.path
                });
            }
        }),
    discount_cycle: yup.number()
        .typeError("Please enter a valid cycle")
        .min(1, "Please enter a valid cycle")
}
